import React, { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './ui/command';
import { Check } from 'lucide-react';
import { cn } from '../lib/utils';

const SearchableSelect = ({ value, onChange, options, placeholder, icon: Icon, testId, accentColor = 'blue', disabled = false }) => {
  const [open, setOpen] = useState(false);
  const selectedLabel = options.find(o => o.value === value)?.label || '';
  const triggerRef = useRef(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full pl-12 h-12 bg-slate-50 border border-slate-200 rounded-xl text-sm text-left relative flex items-center pr-10 outline-none transition-colors cursor-pointer",
            open && `border-${accentColor}-500 bg-white`,
            disabled && "opacity-50 cursor-not-allowed",
            !value && "text-slate-400"
          )}
          data-testid={testId}
        >
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Icon className="text-slate-400" size={20} />
          </div>
          <span className={cn("truncate", value ? "text-slate-900" : "text-slate-400")}>
            {selectedLabel || placeholder}
          </span>
          <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 border-slate-200 shadow-lg rounded-xl overflow-hidden"
        style={{ width: triggerWidth || 'var(--radix-popover-trigger-width)' }}
        align="start"
      >
        <Command shouldFilter={true}>
          <CommandInput placeholder={`Search ${placeholder.toLowerCase().replace('select ', '')}...`} className="h-10" />
          <CommandList className="max-h-52">
            <CommandEmpty className="py-4 text-center text-sm text-slate-500">No results found</CommandEmpty>
            <CommandGroup>
              {options.map(opt => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className="cursor-pointer py-2.5 px-3"
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === opt.value ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableSelect;
